import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { DemoProfile, DemoView, Usertype } from './types';
import { trackDemoEvent } from './demoTracking';
import { JOURNEY_LABEL, JOURNEYS_BY_ENTITY } from './utils';
import { resetManualCompletions } from './onboardingProgress';
import { resetQuestionnaire } from './handlers/questionnaire';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronUpIcon from 'modules/common/components/Icons/ChevronUpIcon';
import { ONBOARDING_QUESTIONNAIRE_QUERY_KEY } from 'services/onboarding/queries/useOnboardingQuestionnaire';

/**
 * Demo banner. "View" maps to member.stage (chronological): Onboarding launches the journey's first
 * flow; Launchpad (checklist) and Activated (dashboard) write ?view&journey and reload.
 */

const VIEW_OPTIONS: readonly DemoView[] = ['onboarding', 'launchpad', 'activated'];

const VIEW_LABEL: Record<DemoView, string> = {
    onboarding: 'Onboarding',
    launchpad: 'Launchpad',
    activated: 'Activated',
};

// First flow by journey: forming a new entity (SP → LLC/S Corp) = formation; takeover = verification.
const FORMATION_FLOW_ROUTE = '/onboarding/entity-formation';
const BUSINESS_VERIFICATION_FLOW_ROUTE = '/onboarding/business-verification';

function isFormationJourney(journey: Usertype): boolean {
    return journey === 'SP-LLC' || journey === 'SP-SC';
}

function applyState(control: 'view' | 'journey', view: DemoView, journey: Usertype) {
    // beacon so it survives the reload below.
    trackDemoEvent('Demo Control Changed', { control, value: control === 'view' ? view : journey }, { beacon: true });
    const params = new URLSearchParams(window.location.search);
    params.set('view', view);
    params.set('journey', journey);
    window.location.search = params.toString(); // full reload
}

interface ControlDropdownProps<T extends string> {
    label: string;
    value: T;
    options: readonly T[];
    renderLabel: (value: T) => string;
    onSelect: (value: T) => void;
}

function ControlDropdown<T extends string>({
    label,
    value,
    options,
    renderLabel,
    onSelect,
}: Readonly<ControlDropdownProps<T>>) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    return (
        <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Button
                variant="text"
                size="small"
                color="inherit"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                endIcon={open ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
                sx={{ color: 'text.primary', fontWeight: 600, textTransform: 'none', px: 1 }}>
                {renderLabel(value)}
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} transitionDuration={0}>
                {options.map((option) => (
                    <MenuItem
                        key={option}
                        selected={option === value}
                        onClick={() => {
                            onSelect(option);
                            setAnchorEl(null);
                        }}>
                        {renderLabel(option)}
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    );
}

/**
 * The bar's rendered height (8px top padding + the 40px control row). Prod's Layout — and the
 * drawers inside it — size themselves against `100dvh`, unaware that the demo stacks this bar above
 * them, so DemoApp subtracts this value to keep the app inside the viewport. Keep it in sync if the
 * bar's padding or control size changes.
 */
export const DEMO_CONTROL_BAR_HEIGHT = 48;

export default function DemoControlBar({ profile }: Readonly<{ profile: DemoProfile }>) {
    const history = useHistory();
    const queryClient = useQueryClient();
    const journeys = JOURNEYS_BY_ENTITY[profile.entity_type];

    const handleViewSelect = (view: DemoView) => {
        // 'onboarding' isn't persisted — it just launches the journey's first flow (full-screen).
        if (view === 'onboarding') {
            trackDemoEvent('Demo Control Changed', { control: 'view', value: 'onboarding' });
            resetManualCompletions(); // start fresh — no leftover completed steps on the dashboard
            // Both flows render inside the demo (formation = the entity formation flow, business
            // verification = DemoBusinessVerification), so SPA-nav from any view; they read journey.
            if (isFormationJourney(profile.journey)) {
                // A fresh walkthrough: reset the mocked application and drop the app's cached copy of it.
                resetQuestionnaire();
                queryClient.removeQueries({ queryKey: ONBOARDING_QUESTIONNAIRE_QUERY_KEY });
                history.push(FORMATION_FLOW_ROUTE);
            } else {
                history.push(BUSINESS_VERIFICATION_FLOW_ROUTE);
            }
            return;
        }
        applyState('view', view, profile.journey);
    };

    return (
        <Box
            sx={{
                flexShrink: 0,
                width: '100%',
                bgcolor: 'cement.50', // match the sidebar / Layout background
                boxShadow: 'none',
                px: { xs: 2, md: 3 },
                pt: 1,
                pb: 0, // section below has its own top margin
                zIndex: (theme) => theme.zIndex.appBar + 1,
            }}>
            {/* Right-aligned so the controls clear the Layout's fixed left sidebar. */}
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <ControlDropdown
                        label="View"
                        value={profile.view}
                        options={VIEW_OPTIONS}
                        renderLabel={(view) => VIEW_LABEL[view]}
                        onSelect={handleViewSelect}
                    />
                    {/* Always available (incl. Activated) so the journey can be set before
                        switching to Onboarding, without toggling to Launchpad first. */}
                    <ControlDropdown
                        label="Journey"
                        value={profile.journey}
                        options={journeys}
                        renderLabel={(journey) => JOURNEY_LABEL[journey]}
                        onSelect={(journey) => applyState('journey', profile.view, journey)}
                    />
                </Stack>
            </Stack>
        </Box>
    );
}
